import React from 'react';

const EditProfileSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
    <style jsx>{`
      @keyframes shimmer {
        0% {
          background-position: -1000px 0;
        }
        100% {
          background-position: 1000px 0;
        }
      }
      
      .skeleton-shimmer {
        animation: shimmer 2s infinite linear;
        background: linear-gradient(
          to right,
          #e0e0e0 0%,
          #f5f5f5 20%,
          #e0e0e0 40%,
          #e0e0e0 100%
        );
        background-size: 1000px 100%;
      }

      @keyframes rotate-gradient {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .gradient-border-skeleton {
        background: conic-gradient(
          from 0deg,
          #feda75,
          #fa7e1e,
          #d62976,
          #962fbf,
          #4f5bd5,
          #feda75
        );
        animation: rotate-gradient 3s linear infinite;
        padding: 3px;
        border-radius: 50%;
        display: inline-block;
      }
    `}</style>

    {/* Navbar Skeleton */}
    <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 z-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Back button and title */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full skeleton-shimmer" />
            <div className="h-6 w-28 skeleton-shimmer rounded-lg" />
          </div>
          
          {/* Right side - empty space for potential unsaved changes indicator */}
          <div className="w-32 h-5" />
        </div>
      </div>
    </nav>

    <div className="pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-12 px-3 sm:px-4 md:px-6 lg:px-8">
      <div className="sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto">
        
        {/* Profile Section Skeleton */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden mb-6 sm:mb-8">
          <div className="p-4 sm:p-6 md:p-8">
            {/* Mobile: Vertical centered layout, Desktop: Horizontal layout */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-8 items-center sm:items-start">
              
              {/* Profile Picture Skeleton with Gradient Border */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {/* Gradient border effect matching ProfileSection */}
                  <div className="gradient-border-skeleton">
                    <div className="w-28 h-28 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 rounded-full overflow-hidden bg-white p-[3px]">
                      <div className="w-full h-full rounded-full skeleton-shimmer" />
                    </div>
                  </div>
                  
                  {/* Camera icon skeleton */}
                  <div className="absolute bottom-1 right-1 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full shadow-md border-2 border-gray-200 flex items-center justify-center">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer" />
                  </div>
                </div>
              </div>

              {/* Profile Info Skeleton - Centered on mobile, left-aligned on desktop */}
              <div className="flex-1 min-w-0 w-full text-center sm:text-left break-words overflow-hidden">
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {/* Name and Username skeleton - Centered on mobile */}
                  <div>
                    <div className="h-6 sm:h-7 md:h-8 w-40 sm:w-48 md:w-56 skeleton-shimmer rounded-lg mb-1 sm:mb-2 mx-auto sm:mx-0" />
                    <div className="h-4 sm:h-5 md:h-6 w-28 sm:w-32 md:w-36 skeleton-shimmer rounded-lg mx-auto sm:mx-0" />
                  </div>

                  {/* Bio skeleton - Centered on mobile */}
                  <div className="space-y-2">
                    <div className="h-3 sm:h-4 w-full skeleton-shimmer rounded-lg" />
                    <div className="h-3 sm:h-4 w-4/5 skeleton-shimmer rounded-lg mx-auto sm:mx-0" />
                  </div>

                  {/* Location & Website skeleton - Centered on mobile, left on desktop */}
                  <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 justify-center sm:justify-start">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer flex-shrink-0" />
                      <div className="h-3 sm:h-4 w-20 sm:w-24 skeleton-shimmer rounded" />
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer flex-shrink-0" />
                      <div className="h-3 sm:h-4 w-24 sm:w-28 skeleton-shimmer rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs Skeleton */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg mb-6 sm:mb-8 overflow-hidden">
          <div className="flex">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex-1 flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 md:py-4 px-2 sm:px-4 md:px-6"
              >
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded skeleton-shimmer" />
                <div className="hidden sm:block h-4 w-20 md:w-24 skeleton-shimmer rounded" />
                <div className="sm:hidden h-4 w-12 skeleton-shimmer rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Form Content Skeleton - Account Details Card */}
        <div className="space-y-6 sm:space-y-8">
          {/* Account Details Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden">
            {/* Card Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded skeleton-shimmer" />
                <div className="h-5 sm:h-6 md:h-7 w-32 sm:w-40 skeleton-shimmer rounded-lg" />
              </div>
              <div className="mt-1 h-3 sm:h-4 w-40 sm:w-48 skeleton-shimmer rounded" />
            </div>
            
            {/* Card Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Grid for Username and Name */}
              <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                {/* Username field */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="h-3 sm:h-4 w-16 sm:w-20 skeleton-shimmer rounded" />
                  <div className="h-10 sm:h-11 w-full skeleton-shimmer rounded-lg" />
                </div>
                
                {/* Name field */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="h-3 sm:h-4 w-12 sm:w-16 skeleton-shimmer rounded" />
                  <div className="h-10 sm:h-11 w-full skeleton-shimmer rounded-lg" />
                </div>
                
                {/* Email field - Full width */}
                <div className="md:col-span-2 space-y-1 sm:space-y-2">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer" />
                    <div className="h-3 sm:h-4 w-24 sm:w-28 skeleton-shimmer rounded" />
                  </div>
                  <div className="h-10 sm:h-11 w-full skeleton-shimmer rounded-lg" />
                </div>
              </div>
            </div>
          </div>

          {/* Profile Information Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden">
            {/* Card Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded skeleton-shimmer" />
                <div className="h-5 sm:h-6 md:h-7 w-36 sm:w-44 skeleton-shimmer rounded-lg" />
              </div>
              <div className="mt-1 h-3 sm:h-4 w-48 sm:w-56 skeleton-shimmer rounded" />
            </div>
            
            {/* Card Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Bio textarea */}
              <div className="space-y-1 sm:space-y-2">
                <div className="h-3 sm:h-4 w-8 sm:w-12 skeleton-shimmer rounded" />
                <div className="h-20 sm:h-24 md:h-28 w-full skeleton-shimmer rounded-lg" />
                <div className="h-3 w-24 skeleton-shimmer rounded" />
              </div>

              {/* Grid for Location and Website */}
              <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                {/* Location field */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer" />
                    <div className="h-3 sm:h-4 w-16 sm:w-20 skeleton-shimmer rounded" />
                  </div>
                  <div className="h-10 sm:h-11 w-full skeleton-shimmer rounded-lg" />
                </div>
                
                {/* Website field */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded skeleton-shimmer" />
                    <div className="h-3 sm:h-4 w-16 sm:w-20 skeleton-shimmer rounded" />
                  </div>
                  <div className="h-10 sm:h-11 w-full skeleton-shimmer rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Action Buttons Skeleton */}
        <div className="fixed bottom-4 sm:bottom-6 right-3 sm:right-6 flex gap-2 sm:gap-3 z-40">
          {/* Cancel button skeleton */}
          <div className="w-20 sm:w-24 md:w-28 h-9 sm:h-10 md:h-12 skeleton-shimmer rounded-full" />
          {/* Save button skeleton */}
          <div className="w-24 sm:w-32 md:w-36 h-9 sm:h-10 md:h-12 skeleton-shimmer rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

export default EditProfileSkeleton;